/** Registered tool origins are the trust boundary for deep links and permissions. */
export const TOOLS: Record<string, { name: string; base: string; sso: boolean }> = {
  scripts: { name: 'Scripts', base: 'https://scripts.sheahanstudios.com', sso: true },
  callsheets: { name: 'Call sheets', base: 'https://callsheets.sheahanstudios.com', sso: true },
  reviews: { name: 'Reviews', base: 'https://review.sheahanstudios.com', sso: true },
  proposals: { name: 'Proposals', base: 'https://proposals.sheahanstudios.com', sso: true },
  media: { name: 'Media', base: 'https://media.sheahanstudios.com', sso: true },
};

export function toolConfig(tool: string) {
  return Object.hasOwn(TOOLS, tool) ? TOOLS[tool] : undefined;
}

export function hasMobileWorkspace(tool: string): boolean {
  return ['scripts', 'callsheets', 'proposals'].includes(tool);
}

/** Reject lookalike origins, credentials and network-path redirects. */
export function toolTarget(tool: string, requested: string | null): URL | null {
  const config = toolConfig(tool);
  if (!config || !requested) return null;
  try {
    const url = new URL(requested);
    if (url.origin !== config.base || url.username || url.password || url.pathname.startsWith('//')) return null;
    return url;
  } catch { return null; }
}

export function toolEntry(tool: string, requested: string | null, embedded: boolean, theme: 'light' | 'dark'): string {
  const config = toolConfig(tool);
  if (!config) return '';
  const url = toolTarget(tool, requested) ?? new URL('/admin', config.base);
  if (hasMobileWorkspace(tool)) {
    // The frame theme is an entry hint; standalone keeps its own preference.
    url.searchParams.delete('osEmbed');
    url.searchParams.delete('osTheme');
    if (embedded) {
      url.searchParams.set('osEmbed', '1');
      url.searchParams.set('osTheme', theme);
    }
  }
  const next = url.pathname + url.search + url.hash;
  return config.sso ? `/api/admin/sso/${tool}?next=${encodeURIComponent(next)}` : url.href;
}

export function toolPermissions(tool: string): string {
  const config = toolConfig(tool);
  if (!config) return '';
  return `clipboard-write ${config.base}${tool === 'scripts' ? `; microphone ${config.base}` : ''}`;
}

/** Return destinations stay in the OS; never pass them to a tool. */
export function documentsReturn(requested: string | null): string | null {
  if (!requested || !/^\/documents(?:\?|$)/.test(requested)) return null;
  try {
    const url = new URL(requested, 'https://os.invalid');
    if (url.origin !== 'https://os.invalid' || url.pathname !== '/documents' || url.hash) return null;
    return url.pathname + url.search;
  } catch { return null; }
}

/** A call sheet's admin URL opened in the embedded Call sheets tool, the same
    route the project rail's "Open ↗" uses (2F timeline, wrap notes, holds). */
export function callsheetHref(url: string): string {
  return `/tools/callsheets?url=${encodeURIComponent(url)}`;
}
